// Main layout component with navigation
// CHANGES:
// - Changed app name from "Gestion de Tâches" to "Tasq"
// - Removed icon/logo, displaying only text name
// - Moved navigation to left sidebar for desktop displays (hidden on mobile)
// - Maintained mobile navigation behavior with top hamburger menu
// - Applied modern dashboard design with new color palette and styling
// - Reorganized sidebar footer: user info with styled role badge, vertically stacked buttons
// - Removed Planning menu item from all roles
// - Harmonized with UnifiedView design: removed max-w constraint, improved spacing, modern shadows
// - Replaced footer buttons with dropdown menu for cleaner UI
// REFACTORED: Extracted sidebar to reusable AppSidebar component for consistency across the app
// UPDATED: Applied uniform px-8 py-8 padding to all pages including team view for consistent UX
// REFACTORED: Now uses RoleBasedLayout for unified navigation across all roles and viewports
// CHANGE: Director role now has same navigation items as admin for read-only supervision
// UPDATED: Now uses centralized navigationConfig for consistent routing and supports mobile bottom nav for all roles
// UPDATED: Removed collapsible sidebar feature for simplicity - sidebar now has fixed width
// UPDATED: Removed unread incident badge feature

import { useAuth } from '../contexts/AuthContext';
import { RoleBasedLayout } from './shared/RoleBasedLayout';
import { NavItem } from './shared/AppSidebar';
import { getFullName, getUserInitials } from '../lib/supabase';
import { type UserRole } from '../lib/roleThemes';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { getBottomNavItems, getSidebarItems } from '../lib/navigationConfig';

interface LayoutProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
}

export function Layout({ children, currentView, onNavigate }: LayoutProps) {
  const { user, logout } = useAuth();
  const { isMobile } = useResponsiveLayout();

  if (!user) return null;

  const displayName = getFullName(user);
  const initials = getUserInitials(user);

  const getNavigationItems = (): NavItem[] => {
    const items = isMobile ? getBottomNavItems(user.role) : getSidebarItems(user.role);

    return items.map(item => {
      const id = item.path === '/' ? 'tasks' : item.path.replace('/', '');
      return {
        id,
        label: item.label,
        icon: item.icon,
      };
    });
  };

  const navItems = getNavigationItems();

  return (
    <RoleBasedLayout
      currentView={currentView}
      onNavigate={onNavigate}
      navItems={navItems}
      userName={displayName}
      userInitials={initials}
      userRole={user.role as UserRole}
      onLogout={logout}
    >
      {children}
    </RoleBasedLayout>
  );
}
